import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { canModifyContent } from '@/lib/account-roles';
import { useAllVendorsQuery } from '@/queries/vendors';
import { useAuthStore } from '@/stores/auth-store';
import {
  HOME_TABS,
  useHomeDashboardStore,
} from '@/stores/home-dashboard-store';
import { Loading } from '../controls';

function InfoBlock({ icon, title, children }) {
  if (!children) {
    return null;
  }
  return (
    <section className="bethere-info-block">
      <h2 className="flex items-center gap-2 text-base font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={icon}
          className="text-[var(--bethere-primary)]"
        />
        {title}
      </h2>
      {children}
    </section>
  );
}

function formatHref(url) {
  if (!url?.trim()) {
    return null;
  }
  const v = url.trim();
  return v.includes('://') ? v : `https://${v}`;
}

export function VendorView(props) {
  const vendorId = props.match?.params?.id;
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);
  const setActiveTab = useHomeDashboardStore((s) => s.setActiveTab);

  const vendorsQ = useAllVendorsQuery();

  const vendor = useMemo(() => {
    const list = vendorsQ.data ?? [];
    return list.find((v) => String(v.vendorId) === String(vendorId)) ?? null;
  }, [vendorsQ.data, vendorId]);

  if (vendorsQ.isPending) {
    return <Loading />;
  }

  if (vendorsQ.isError || !vendor) {
    return (
      <div className="py-16 text-center text-red-600">
        Could not load this vendor.{' '}
        <Link
          to="/"
          className="font-bold text-[var(--bethere-primary)]"
          onClick={() => setActiveTab(HOME_TABS.vendors)}
        >
          Back to vendors
        </Link>
      </div>
    );
  }

  const links = vendor.links ?? {};
  const webHref = formatHref(links.web);
  const categoryLine = [vendor.category, vendor.subcategory]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="bethere-festival-view">
      <nav className="breadcrumb-container" aria-label="Breadcrumb">
        <ul className="breadcrumb m-0 list-none p-0">
          <li>
            <Link to="/" onClick={() => setActiveTab(HOME_TABS.vendors)}>
              My Vendors
            </Link>
          </li>
          <li aria-current="page">{vendor.vendorName}</li>
        </ul>
        {canModify && (
          <Link
            to={`/vendor/${vendor.vendorId}`}
            className="bethere-view-edit-link"
          >
            <FontAwesomeIcon icon={['fas', 'pen-to-square']} /> Edit vendor
          </Link>
        )}
      </nav>

      <div className="detail-hero">
        {vendor.photoURL ? (
          <img
            src={vendor.photoURL}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-700 text-white/70">
            No image
          </div>
        )}
      </div>

      <div className="bethere-content-container">
        <div className="event-main-info min-w-0">
          <div className="glass-panel p-6 sm:p-8">
            <h1 className="mb-6 text-[length:var(--bethere-f-h1)] font-bold leading-[1.2] text-[#121212]">
              {vendor.vendorName}
            </h1>

            {vendor.vendorDescription && (
              <InfoBlock
                icon={['fas', 'circle-info']}
                title="General Information"
              >
                <p className="m-0 whitespace-pre-wrap leading-relaxed text-[var(--bethere-f-p)] text-[#121212]">
                  {vendor.vendorDescription}
                </p>
              </InfoBlock>
            )}

            {!vendor.vendorDescription && (
              <p className="m-0 text-[var(--muted-foreground)]">
                No description yet.
              </p>
            )}
          </div>
        </div>

        <aside className="bethere-detail-sidebar">
          <div className="glass-panel p-6">
            <ul className="meta-list m-0 list-none p-0">
              {categoryLine && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'tags']} />
                  <div>
                    <strong>Category</strong>
                    {categoryLine}
                  </div>
                </li>
              )}
              {vendor.vendorContact && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'user']} />
                  <div>
                    <strong>Contact</strong>
                    {vendor.vendorContact}
                  </div>
                </li>
              )}
              {(webHref ||
                links.facebook ||
                links.twitter ||
                links.instagram) && (
                <li className="event-socials">
                  <FontAwesomeIcon icon={['fas', 'link']} />
                  <div>
                    <strong>Website</strong>
                    {webHref && (
                      <a
                        href={webHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {links.web}
                      </a>
                    )}
                    <div className="event-socials-icons">
                      {links.facebook && (
                        <a
                          href={formatHref(links.facebook)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                        >
                          <FontAwesomeIcon icon={['fab', 'facebook']} />
                        </a>
                      )}
                      {links.twitter && (
                        <a
                          href={formatHref(links.twitter)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="X"
                        >
                          <FontAwesomeIcon icon={['fab', 'x-twitter']} />
                        </a>
                      )}
                      {links.instagram && (
                        <a
                          href={formatHref(links.instagram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                        >
                          <FontAwesomeIcon icon={['fab', 'instagram']} />
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </article>
  );
}
